import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { getCurrentUserInfo, login } from "@/api/orval";
import { User } from "@/types/user";

// Auth cache keys. Kept hand-written because login/logout manage the JWT in
// localStorage and seed/clear the user query, which the generated hooks don't do.
export const AUTH_KEYS = {
  user: ["user"] as const,
  auth: ["auth"] as const,
} as const;

export const useLoginMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (credentials: { email: string; password: string }) => {
      const data = await login(credentials);
      // Persist the token here, before any onSuccess fires. The global
      // MutationCache.onSuccess invalidates all queries and runs before this
      // mutation's onSuccess, so if the token were set later the user query
      // would refetch tokenless and stay unauthenticated until a reload.
      localStorage.setItem("access_token", data.access_token);
      return data;
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: AUTH_KEYS.user });
      queryClient.setQueryData(AUTH_KEYS.auth, { isAuthenticated: true });
    },
  });
};

export const useLogoutMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // Logout is client-side only: discard the token and clear caches.
    mutationFn: async () => {
      localStorage.removeItem("access_token");
    },
    onSuccess: () => {
      queryClient.setQueryData(AUTH_KEYS.user, null);
      queryClient.removeQueries({ queryKey: AUTH_KEYS.user });
      queryClient.setQueryData(AUTH_KEYS.auth, { isAuthenticated: false });
      queryClient.clear();
    },
  });
};

export const useCurrentUser = () => {
  return useQuery<User>({
    queryKey: AUTH_KEYS.user,
    queryFn: async () => {
      const token = localStorage.getItem("access_token");
      if (!token) {
        throw new Error("Not authenticated");
      }
      return (await getCurrentUserInfo()) as unknown as User;
    },
    retry: false,
    retryOnMount: false,
    refetchOnWindowFocus: false,
  });
};
